import { NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  input,
  linkedSignal,
  signal,
  viewChildren,
} from '@angular/core';
import { ILpActivity, NineSlicedContainerComponent, Rect, Vector2 } from '@gametailors/leerplus';
import { LpRidScrapbookReward, LpRidScrapbookViewerActivity } from '../../../../typings/schema';
import { RidButton } from '../../../components/rid-button/rid-button';
import { RidChevron } from '../../../components/rid-chevron/rid-chevron';
import { RidModalBackground } from '../../../components/rid-modal-background/rid-modal-background';
import { AudioEffectPlayer } from '../../../utils/audio.utils';
import { RidDrawingTool, RidDrawingTray } from '../../components/rid-drawing-tray/rid-drawing-tray';
import { RidScrapbookDrawingInput } from '../../components/rid-scrapbook-drawing-input/rid-scrapbook-drawing-input';
import { RidScrapbookDrawing } from '../../components/rid-scrapbook-drawing/rid-scrapbook-drawing';
import { LEERPLUS } from '../../rid-game.utils';
import { RidPlacedReward, RidRewardService } from '../../services/rid-reward.service';
import { RidScrapbookService } from '../../services/rid-scrapbook.service';

const REWARD_HEIGHT = 96;

/**
 * A sheet that is being turned. Sheet `index` has the right page of week `index` on its front and the left page of week `index + 1` on its back.
 * Every sheet uses the same animation (0deg -> -180deg); turning back just plays it in reverse, so a sheet can be reversed mid-flight.
 */
interface TurningSheet {
  id: number;
  index: number;
  startedForward: boolean;
  forward: boolean;
}

/** A sheet in the DOM: either turning, or lying hidden next to the open spread, ready to be turned. */
interface RenderedSheet {
  index: number;
  turn: TurningSheet | null;
}

@Component({
  selector: 'rid-scrapbook-viewer-activity',
  imports: [
    RidModalBackground,
    NineSlicedContainerComponent,
    RidButton,
    RidChevron,
    NgTemplateOutlet,
    RidDrawingTray,
    RidScrapbookDrawing,
    RidScrapbookDrawingInput,
  ],
  templateUrl: './rid-scrapbook-viewer-activity.html',
  styleUrl: './rid-scrapbook-viewer-activity.css',
})
export class RidScrapbookViewerActivity implements ILpActivity<LpRidScrapbookViewerActivity> {
  protected readonly scrapbook = inject(RidScrapbookService);
  protected readonly $lpApiService = inject(LEERPLUS.apiToken);
  private readonly rewardService = inject(RidRewardService);
  private readonly injector = inject(Injector);

  activity = input.required<LpRidScrapbookViewerActivity>();

  protected readonly pageCount = this.scrapbook.weekCount;

  private readonly initialPageIndex = computed(() => Math.min(Math.max(this.scrapbook.currentWeekIndex(), 0), this.pageCount() - 1));

  // the sheets in the air are always sheets leftPageIndex .. rightPageIndex - 1, so what lies below them is:
  // the left page of week leftPageIndex, and the right page of week rightPageIndex (both equal when nothing is turning)
  protected readonly leftPageIndex = linkedSignal(() => this.initialPageIndex());
  protected readonly rightPageIndex = linkedSignal(() => this.initialPageIndex());
  protected readonly sheets = signal<TurningSheet[]>([]);

  // the sheets that can be turned next are rendered (hidden) ahead of time: the paper sprite is drawn on a canvas a frame after it is created,
  // so a freshly created sheet would show the page underneath it for its first frame
  protected readonly renderedSheets = computed<RenderedSheet[]>(() => {
    const turning = this.sheets();
    const indices = new Set([...turning.map((s) => s.index), this.leftPageIndex() - 1, this.rightPageIndex()]);
    // sorted, so existing sheets are never moved in the DOM (which would restart their animations)
    return [...indices]
      .filter((i) => i >= 0 && i < this.pageCount() - 1)
      .sort((a, b) => a - b)
      .map((index) => ({ index, turn: turning.find((s) => s.index === index) ?? null }));
  });

  // the week the book ends up on once all sheets have landed
  protected readonly targetPageIndex = computed(() => this.leftPageIndex() + this.sheets().filter((s) => s.forward).length);

  protected readonly drawingTool = signal<RidDrawingTool | null>(null);
  protected readonly zoomedReward = signal<LpRidScrapbookReward | null>(null);
  // drawing and zooming in only while the book lies still, since the pages underneath show different weeks while turning
  protected readonly isBookStill = computed(() => !this.sheets().length);

  private readonly sheetEls = viewChildren<ElementRef<HTMLElement>>('sheetEl');
  private sheetId = 0;

  private readonly rewardsByWeek = computed(() => {
    const byWeek = new Map<number, RidPlacedReward[]>();
    for (const r of this.rewardService.placedRewardsIn(this.scrapbook.items())) {
      const entries = byWeek.get(r.week) ?? [];
      entries.push(r);
      byWeek.set(r.week, entries);
    }
    return byWeek;
  });

  protected readonly bookUrl = computed(() => this.$lpApiService.getAssetUrl(this.activity().book.image));
  protected readonly bookSize = computed<Vector2>(() => {
    const { x, y } = this.activity().book.size;
    return [x, y] as Vector2;
  });
  protected readonly bookContentBox = computed<Rect>(() =>
    this.toPixelRect(this.activity().book.contentBoxMin, this.activity().book.contentBoxMax),
  );
  protected readonly bookPaddingBox = computed<Rect>(() =>
    this.toPixelRect(this.activity().book.paddingBoxMin, this.activity().book.paddingBoxMax),
  );
  protected readonly bookScale = computed(() => this.activity().book.scale);

  // the padding the nine-sliced container puts around its content; used to line the sheet's copy of the sprite up with the book
  protected readonly bookContentInset = computed(() => {
    const content = this.bookContentBox();
    const padding = this.bookPaddingBox();
    const scale = this.bookScale();
    return {
      top: scale * (content.from[1] - padding.from[1]),
      right: scale * (padding.to[0] - content.to[0]),
      bottom: scale * (padding.to[1] - content.to[1]),
      left: scale * (content.from[0] - padding.from[0]),
    };
  });

  nextPage() {
    // undo a sheet that is still turning back before starting a new one
    const turningBack = this.sheets().filter((s) => !s.forward);
    if (turningBack.length) return this.reverse(turningBack.reduce((a, b) => (a.index < b.index ? a : b)));

    const index = this.rightPageIndex();
    if (index >= this.pageCount() - 1) return;
    if (this.prefersReducedMotion()) return this.jumpTo(index + 1);

    this.rightPageIndex.set(index + 1);
    this.startSheet(index, true);
  }

  previousPage() {
    const turningForward = this.sheets().filter((s) => s.forward);
    if (turningForward.length) return this.reverse(turningForward.reduce((a, b) => (a.index > b.index ? a : b)));

    const index = this.leftPageIndex() - 1;
    if (index < 0) return;
    if (this.prefersReducedMotion()) return this.jumpTo(index);

    this.leftPageIndex.set(index);
    this.startSheet(index, false);
  }

  close() {
    this.$lpApiService.finishCurrentActivity();
  }

  private onSheetLanded(id: number) {
    const current = this.sheets().find((s) => s.id === id);
    if (!current) return;

    if (current.forward) this.leftPageIndex.set(current.index + 1);
    else this.rightPageIndex.set(current.index);
    this.sheets.update((sheets) => sheets.filter((s) => s.id !== id));
  }

  protected rewardsFor(week: number): RidPlacedReward[] {
    return this.rewardsByWeek().get(week) ?? [];
  }

  protected rewardHeightFor(reward: LpRidScrapbookReward): number {
    return REWARD_HEIGHT * (reward.scale ?? 1);
  }

  private startSheet(index: number, forward: boolean) {
    AudioEffectPlayer.play('PAGE_FLIP');
    const id = ++this.sheetId;
    this.sheets.update((sheets) => [...sheets, { id, index, startedForward: forward, forward }]);

    // the finished promise also resolves when the animation was reversed back to its start (unlike animationend)
    afterNextRender(
      () => {
        const turn = this.sheetEl(index)?.getAnimations()[0];
        if (!turn) return this.onSheetLanded(id);
        turn.finished.then(() => this.onSheetLanded(id)).catch(() => undefined); // rejects when cancelled on destroy
      },
      { injector: this.injector },
    );
  }

  private reverse(sheet: TurningSheet) {
    // only possible once the sheet has been rendered (i.e. not within the same frame it was started)
    const el = this.sheetEl(sheet.index);
    if (!el) return;
    AudioEffectPlayer.play('PAGE_FLIP');
    el.getAnimations({ subtree: true }).forEach((a) => a.reverse());
    this.sheets.update((sheets) => sheets.map((s) => (s.id === sheet.id ? { ...s, forward: !s.forward } : s)));
  }

  private sheetEl(index: number): HTMLElement | undefined {
    return this.sheetEls().find((e) => e.nativeElement.dataset['sheetIndex'] === `${index}`)?.nativeElement;
  }

  private jumpTo(index: number) {
    this.leftPageIndex.set(index);
    this.rightPageIndex.set(index);
  }

  private prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  private toPixelRect(minFraction: { x: number; y: number }, maxFraction: { x: number; y: number }): Rect {
    const { x: width, y: height } = this.activity().book.size;
    return {
      from: [minFraction.x * width, minFraction.y * height] as Vector2,
      to: [maxFraction.x * width, maxFraction.y * height] as Vector2,
    };
  }
}
